import type {
  Stamp,
  Casting,
  CastingAnswers,
  AiVoice,
  Gender,
  AgeRange,
  Language,
  UseCase,
  Character,
  Tonality,
  Voice,
  VoiceListItem,
  VoiceNameMatch,
  VoiceDetailResponse,
  VoiceUpdateInput,
  VoiceAudio,
  VoiceAudioDraftInput,
  AudioFailure,
  TagInfo,
  VoiceFilters,
  Agentur,
  AgenturInput,
  AgenturListItem,
  AgenturDetail,
  VoiceProject,
  ProjectInput,
  VocabItem,
  VocabKind,
  SelfLinkInfo,
} from '@shared/types';
import type { TagCategory } from '@shared/voiceMeta';
import { xhrUpload, type UploadProgress } from './xhrUpload';

function voicesQueryString(f: VoiceFilters): string {
  const p = new URLSearchParams();
  if (f.search) p.set('search', f.search);
  f.language?.forEach((v) => p.append('language', v));
  f.dialect?.forEach((v) => p.append('dialect', v));
  f.accent?.forEach((v) => p.append('accent', v));
  f.category?.forEach((v) => p.append('category', v));
  if (f.uncategorized) p.set('uncategorized', '1');
  f.gender?.forEach((v) => p.append('gender', v));
  f.voiceAge?.forEach((v) => p.append('voice_age', v));
  f.tag?.forEach((v) => p.append('tag', v));
  f.status?.forEach((v) => p.append('status', v));
  // Default versteckt Sprecher ohne Sprachproben; nur wenn explizit gezeigt werden sollen.
  if (f.hideEmpty === false) p.set('show_empty', '1');
  if (f.favorit) p.set('favorit', '1');
  if (f.brandvoice) p.set('brandvoice', '1');
  if (f.brandvoiceExpiring) p.set('brandvoice_expiring', '1');
  if (f.selfService) p.set('self_service', '1');
  if (f.sort) p.set('sort', f.sort);
  if (f.dir) p.set('dir', f.dir);
  if (f.page) p.set('page', String(f.page));
  if (f.perPage) p.set('per_page', String(f.perPage));
  return p.toString();
}

const API_BASE = '/api';

// Gemeinsame Nachbehandlung für request()/requestMultipart(): Redirect-Erkennung
// (abgelaufene Anmeldung; voller Reload NUR auf /admin — öffentliche Seiten /self, /c
// haben keine Anmeldung, dort würde ein Reload einen Endlos-Loop erzeugen), HTTP-Fehler → Error mit .status,
// sonst JSON. Vorher in beiden Funktionen dupliziert.
async function finishResponse<T>(res: Response): Promise<T> {
  if (res.type === 'opaqueredirect' || res.status === 0) {
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
      window.location.reload();
      return new Promise<T>(() => {});
    }
    throw new Error('unauthorized');
  }
  if (!res.ok) {
    const text = await res.text();
    let message = text;
    let parsed: unknown = null;
    try {
      parsed = JSON.parse(text);
      if ((parsed as { error?: unknown })?.error) message = String((parsed as { error?: unknown }).error);
    } catch {}
    const err = new Error(`${res.status} ${message}`);
    (err as { status?: number }).status = res.status;
    (err as { body?: unknown }).body = parsed;
    throw err;
  }
  return res.json() as Promise<T>;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    // Forward Auth (Caddy + Authentik) beantwortet eine abgelaufene Session
    // serverseitig mit einem 302 auf die Anmeldung. Unsere API selbst
    // redirectet nie legitim, also heisst *jede* Redirect-Antwort "Session
    // abgelaufen". redirect: 'manual' verhindert, dass fetch() dem 302
    // automatisch folgt (die Antwort des Auth-Hosts haette keine passenden
    // CORS-Header -> TypeError "Failed to fetch") und liefert stattdessen
    // eine opaque Redirect-Response, die finishResponse() erkennt.
    // (Auf Cloudflare war der Aussteller Zero Trust; Mechanik identisch.)
    redirect: 'manual',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });
  return finishResponse<T>(res);
}

// (Multipart-Uploads laufen jetzt über xhrUpload() für echten Byte-Progress —
// der frühere fetch-basierte requestMultipart wird nicht mehr gebraucht.)

export interface AudioUploadMeta {
  category: string | null;
  language: string;
  dialect: string | null;
  accent: string | null;
  is_demomix: boolean;
  freetext_suffix: string | null;
}

export interface VoicePrefill {
  name: string;
  gender: Gender | null;
  age_range: AgeRange | null;
  languages: Language[];
  characters: Character[];
  raw_labels: Record<string, unknown>;
  preview_url: string | null;
}

export interface VoiceSample {
  filename: string;
  url: string;
  contentType: string;
}

export interface AiVoicePayload {
  elevenlabs_id: string;
  gender: Gender;
  age_range: AgeRange;
  languages: Language[];
  use_cases: UseCase[];
  characters: Character[];
  tonality: Tonality[];
}

export interface AiVoiceUpdatePayload {
  gender: Gender;
  age_range: AgeRange;
  languages: Language[];
  use_cases: UseCase[];
  characters: Character[];
  tonality: Tonality[];
}

export const api = {
  // Sprecher:innen (echte Voices, nicht ElevenLabs-AiVoices)
  listVoices: (f: VoiceFilters) =>
    request<{ voices: VoiceListItem[]; total: number; page: number; per_page: number }>(
      `/admin/voices?${voicesQueryString(f)}`
    ),
  getVoice: (id: number) =>
    request<{ voice: VoiceDetailResponse }>(`/admin/voices/${id}`).then((r) => r.voice),
  // Aktualitaets-Ping (Spec 2026-09-15 §5): nur der Zeitstempel, alle 5 s je
  // offener Bearbeiten-Ansicht — deshalb bewusst NICHT getVoice().
  getVoiceStamp: (id: number) =>
    request<Stamp>(`/admin/voices/${id}/stamp`),
  // Sammel-Speichern (Task 4): EIN Request fuer Stammdaten + Proben-Entwuerfe.
  // `opts.audios` traegt nur die Proben mit offener Vormerkung; `expected_updated_at`
  // sichert gegen Parallel-Speicherungen ab (409 bei Konflikt), `force` erzwingt trotzdem.
  updateVoice: (
    id: number, input: VoiceUpdateInput,
    opts: { audios?: VoiceAudioDraftInput[]; expected_updated_at?: string; force?: boolean } = {},
  ) =>
    request<{ voice: Voice & { tags: string[]; similar_voices: string[]; found_via: string[]; languages: string[] }; audios: VoiceAudio[]; audio_failures: AudioFailure[] }>(
      `/admin/voices/${id}`, { method: 'PUT', body: JSON.stringify({ ...input, ...opts }) },
    ),
  createVoice: (input: VoiceUpdateInput) =>
    request<{ voice: Voice & { tags: string[]; similar_voices: string[]; found_via: string[]; languages: string[] } }>('/admin/voices', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.voice),
  uploadAudio: (voiceId: number, body: Blob, meta: AudioUploadMeta, originalName: string, onProgress?: (p: UploadProgress) => void) => {
    // `body` ist das bereits client-seitig zu MP3 192k normalisierte Blob (audioConvert.ts);
    // Content-Type audio/mpeg → Worker akzeptiert es. `originalName` (Roh-Dateiname, evtl.
    // .wav) landet nur als original_filename; der Storage-Name kommt aus dem Naming-Schema.
    const p = new URLSearchParams({ language: meta.language, is_demomix: String(meta.is_demomix), filename: originalName });
    if (meta.category) p.set('category', meta.category);
    if (meta.dialect) p.set('dialect', meta.dialect);
    if (meta.accent) p.set('accent', meta.accent);
    if (meta.freetext_suffix) p.set('freetext_suffix', meta.freetext_suffix);
    return xhrUpload<{ audio: VoiceAudio }>(`${API_BASE}/admin/voices/${voiceId}/audios?${p.toString()}`, body, onProgress, 'audio/mpeg').then((r) => r.audio);
  },
  previewAudioName: (voiceId: number, meta: AudioUploadMeta, offset: number) => {
    const p = new URLSearchParams({ language: meta.language, is_demomix: String(meta.is_demomix) });
    if (meta.category) p.set('category', meta.category);
    if (meta.dialect) p.set('dialect', meta.dialect);
    if (meta.accent) p.set('accent', meta.accent);
    if (meta.freetext_suffix) p.set('freetext_suffix', meta.freetext_suffix);
    if (offset) p.set('offset', String(offset));
    return request<{ filename: string }>(`/admin/voices/${voiceId}/audio-name-preview?${p}`).then((r) => r.filename);
  },
  listTags: () => request<{ tags: TagInfo[] }>('/admin/tags').then((r) => r.tags),
  updateTag: (id: number, input: { name: string; tag_category: TagCategory | null }) =>
    request<{ tag: TagInfo }>(`/admin/tags/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }).then((r) => r.tag),
  deleteTag: (id: number) =>
    request<{ ok: true }>(`/admin/tags/${id}`, { method: 'DELETE' }),
  createTag: (name: string, tag_category: TagCategory | null) =>
    request<{ tag: TagInfo }>('/admin/tags', { method: 'POST', body: JSON.stringify({ name, tag_category }) }).then((r) => r.tag),
  // Vokabular (Dialekt/Akzent/Kategorie) — Etappe 5.5.
  listVocabulary: (kind: VocabKind) =>
    request<{ items: VocabItem[] }>(`/admin/vocabulary?kind=${kind}`).then((r) => r.items),
  createVocab: (kind: VocabKind, name: string) =>
    request<{ item: VocabItem }>('/admin/vocabulary', { method: 'POST', body: JSON.stringify({ kind, name }) }).then((r) => r.item),
  deleteVocab: (id: number) =>
    request<{ ok: true }>(`/admin/vocabulary/${id}`, { method: 'DELETE' }).then(() => undefined),
  deleteAudio: (id: number) =>
    request<{ ok: true }>(`/admin/audios/${id}`, { method: 'DELETE' }),
  deleteVoice: (id: number) =>
    request<{ ok: true; failed_files: string[] }>(`/admin/voices/${id}`, { method: 'DELETE' }),
  createProject: (voiceId: number, input: ProjectInput) =>
    request<{ project: VoiceProject }>(`/admin/voices/${voiceId}/projects`, { method: 'POST', body: JSON.stringify(input) }).then((r) => r.project),
  updateProject: (id: number, input: ProjectInput) =>
    request<{ project: VoiceProject }>(`/admin/projects/${id}`, { method: 'PUT', body: JSON.stringify(input) }).then((r) => r.project),
  deleteProject: (id: number) =>
    request<{ ok: true }>(`/admin/projects/${id}`, { method: 'DELETE' }),
  markInvoiceSent: (id: number, lang: 'de' | 'en') =>
    request<{ project: VoiceProject }>(`/admin/projects/${id}/invoice-sent`, { method: 'POST', body: JSON.stringify({ lang }) }).then((r) => r.project),
  listSimilarVoices: () =>
    request<{ items: { value: string; count: number }[] }>('/admin/similar-voices').then((r) => r.items),
  deleteSimilarVoice: (value: string) =>
    request<{ deleted: number }>(`/admin/similar-voices?value=${encodeURIComponent(value)}`, { method: 'DELETE' }).then((r) => r.deleted),
  listFoundVia: () =>
    request<{ items: { value: string; count: number }[] }>('/admin/found-via').then((r) => r.items),
  deleteFoundVia: (value: string) =>
    request<{ deleted: number }>(`/admin/found-via?value=${encodeURIComponent(value)}`, { method: 'DELETE' }).then((r) => r.deleted),

  // Self-Service-Links (Etappe 7, Admin/ZT). Klartext-PW kommt nur aus create/reveal.
  // Idempotent: `existing: true` heisst „es gab schon einen Link" — dann fehlt
  // `password` (das gibt es nur bei der Neuanlage), und geschrieben wurde nichts.
  createSelfLink: (id: number) =>
    request<{ url: string; password?: string; existing: boolean }>(`/admin/voices/${id}/self-link`, { method: 'POST' }),
  getSelfLink: (id: number) =>
    request<SelfLinkInfo>(`/admin/voices/${id}/self-link`),
  revealSelfPw: (id: number) =>
    request<{ password: string }>(`/admin/voices/${id}/self-link/reveal`),
  deleteSelfLink: (id: number) =>
    request<{ ok: true }>(`/admin/voices/${id}/self-link`, { method: 'DELETE' }).then(() => undefined),

  // Agenturen
  listAgenturen: () =>
    request<{ agenturen: AgenturListItem[] }>('/admin/agenturen').then((r) => r.agenturen),
  getAgentur: (id: number) =>
    request<AgenturDetail>(`/admin/agenturen/${id}`),
  createAgentur: (input: AgenturInput) =>
    request<{ agentur: Agentur }>('/admin/agenturen', { method: 'POST', body: JSON.stringify(input) }).then((r) => r.agentur),
  updateAgentur: (id: number, input: AgenturInput) =>
    request<{ agentur: Agentur }>(`/admin/agenturen/${id}`, { method: 'PUT', body: JSON.stringify(input) }).then((r) => r.agentur),
  deleteAgentur: (id: number) =>
    request<{ ok: true }>(`/admin/agenturen/${id}`, { method: 'DELETE' }),

  // Castings
  listCastings: () =>
    request<{ castings: Casting[] }>('/admin/castings').then((r) => r.castings),
  getCasting: (id: number) =>
    request<{ casting: Casting }>(`/admin/castings/${id}`).then((r) => r.casting),
  createCasting: (input: {
    project_name: string;
    customer_name: string;
    recipient_email: string;
    notes?: string | null;
  }) =>
    request<{ casting: Casting }>('/admin/castings', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.casting),
  updateCastingNotes: (id: number, notes: string | null) =>
    request<{ casting: Casting }>(`/admin/castings/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ notes }),
    }).then((r) => r.casting),
  deleteCasting: (id: number) =>
    request<{ ok: true }>(`/admin/castings/${id}`, { method: 'DELETE' }),

  // AI-Voices (ElevenLabs)
  listAiVoices: () =>
    request<{ aivoices: AiVoice[] }>('/admin/aivoices').then((r) => r.aivoices),
  getAiVoice: (id: number) =>
    request<{ aivoice: AiVoice }>(`/admin/aivoices/${id}`).then((r) => r.aivoice),
  lookupElevenLabsVoice: (elevenlabsId: string) =>
    request<{ prefill: VoicePrefill }>(
      `/admin/aivoices/lookup/${encodeURIComponent(elevenlabsId)}`
    ).then((r) => r.prefill),
  createAiVoice: (input: AiVoicePayload) =>
    request<{ aivoice: AiVoice }>('/admin/aivoices', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.aivoice),
  updateAiVoice: (id: number, input: AiVoiceUpdatePayload) =>
    request<{ aivoice: AiVoice }>(`/admin/aivoices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }).then((r) => r.aivoice),
  deleteAiVoice: (id: number) =>
    request<{ ok: true }>(`/admin/aivoices/${id}`, { method: 'DELETE' }),
  listAiVoiceSamples: (voiceId: string) =>
    request<{ voice_id: string; samples: VoiceSample[] }>(
      `/samples/${encodeURIComponent(voiceId)}`
    ).then((r) => r.samples),
  runAvailabilityCheck: () =>
    request<{
      checked: number;
      available: number;
      unavailable: number;
      errors: number;
      details: Array<{ elevenlabs_id: string; status: string; error?: string }>;
    }>('/admin/availability-check', { method: 'POST' }),

  brandvoiceExpiringCount: () =>
    request<{ count: number }>('/admin/brandvoice/expiring').then((r) => r.count),

  checkVoiceName: (lastName: string, firstName: string | null) => {
    const p = new URLSearchParams({ last_name: lastName });
    if (firstName) p.set('first_name', firstName);
    return request<{ matches: VoiceNameMatch[] }>(`/admin/voices/check_name?${p.toString()}`);
  },

  // Public API
  getPublicCasting: (slug: string) =>
    request<{
      casting: {
        id: number;
        slug: string;
        project_name: string;
        customer_name: string;
        status: 'active' | 'completed';
        completed_at: string | null;
      };
    }>(`/casting/${encodeURIComponent(slug)}`).then((r) => r.casting),

  matchCasting: (slug: string, answers: CastingAnswers) =>
    request<{ voices: Array<{ voice: AiVoice; score: number; scorePercent: number }> }>(
      `/casting/${encodeURIComponent(slug)}/match`,
      { method: 'POST', body: JSON.stringify(answers) }
    ).then((r) => r.voices),

  completeCasting: (slug: string, input: {
    answers: CastingAnswers;
    selected_voices: string[];
  }) =>
    request<{
      casting: Casting;
      mail: { subject: string; body: string; mailto_url: string };
    }>(
      `/casting/${encodeURIComponent(slug)}/complete`,
      { method: 'POST', body: JSON.stringify(input) }
    ),
};
